import { LanguageSelect, t } from './i18n';
import AdminMaintenanceGate from './components/AdminMaintenanceGate';
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  useNavigate,
  useLocation,
  Navigate,
} from 'react-router-dom';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import TableManagement from './pages/TableManagement';
import MarqueeManagement from './pages/MarqueeManagement';
import Unauthorized from './pages/Unauthorized';
import AssetLedger from './pages/AssetLedger';
import PaymentPage from './pages/PaymentPage';
import LivePaymentOperations from './pages/LivePaymentOperations';
import PaymentPolicySettings from './pages/PaymentPolicySettings';
import LoungeManagement from './pages/LoungeManagement';
import LoungePermissionSettings from './pages/LoungePermissionSettings';
import { fetchMyLoungePermissions } from './api/adminPermissionApi';
import NoticeManagement from './pages/NoticeManagement';
import AdBannerManagement from './pages/AdBannerManagement';
import EmailManagement from './pages/EmailManagement';
import AgentReferralManagement from './pages/AgentReferralManagement';
import DailyMissionManagement from './pages/DailyMissionManagement';
import OperationsDashboard from './pages/OperationsDashboard';
import AdminAssetSummary from './components/AdminAssetSummary';
import { clearAdminSession, getAdminToken, getStoredAdminUser, isAdminTokenValid } from './auth/adminSession';
import {
  LoungePermissionRoute,
  OperationsDashboardPermissionRoute,
  AuthRoute,
  LevelRoute,
  SuperAdminRoute,
  isSuperAdminUser,
} from './routes/ProtectedRoutes';

const ADMIN_LOGO_PATH = '/assets/night-poker-business-logo.png';

function getStoredUser() {
  return getStoredAdminUser();
}

function BrandLogo({ compact = false }) {
  const [failed, setFailed] = React.useState(false);

  return (
    <span className={`admin-brand-logo ${compact ? 'is-compact' : ''}`}>
      {!failed ? (
        <img
          src={ADMIN_LOGO_PATH}
          alt="NIGHT Poker Business"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="admin-brand-fallback">NIGHT Poker Business</span>
      )}
    </span>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const storedToken = getAdminToken();
  const token = storedToken && isAdminTokenValid(storedToken) ? storedToken : '';
  const user = getStoredUser();
  const userLevel = String(user?.user_level || '').trim().toLowerCase();
  const isSuperAdmin = isSuperAdminUser(user);
  // 운영 대시보드는 모든 유효한 LV1~LV4 관리자의 기본 메뉴다.
  // 조직별 통계 범위는 백엔드에서 로그인한 AgentNode를 기준으로 강제한다.
  const hasOperationsDashboard = isSuperAdmin || ['lv2', 'lv3', 'lv4'].includes(userLevel);
  const homePath = hasOperationsDashboard ? '/operations-dashboard' : '/dashboard';
  const [loungeCanView, setLoungeCanView] = React.useState(false);
  React.useEffect(() => {
    if (!token) { setLoungeCanView(false); return undefined; }
    const abort = new AbortController();
    fetchMyLoungePermissions(abort.signal).then((data) => {
      if (!abort.signal.aborted) {
        setLoungeCanView(data.permissions?.view === true);
      }
    }).catch(() => { if (!abort.signal.aborted) setLoungeCanView(false); });
    return () => abort.abort();
  }, [token, location.pathname]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isDesktopMoreOpen, setIsDesktopMoreOpen] = React.useState(false);
  const [desktopPrimaryCount, setDesktopPrimaryCount] = React.useState(4);
  const mobileMenuRef = React.useRef(null);
  const desktopMoreRef = React.useRef(null);
  const desktopNavRef = React.useRef(null);
  const desktopMeasureRef = React.useRef(null);

  const hideNav = ['/login', '/signup'].includes(location.pathname) || !token;

  const handleLogout = () => {
    clearAdminSession();
    setIsMobileMenuOpen(false);
    navigate('/login');
  };

  const menuItems = [
    { to: '/operations-dashboard', label: t('운영 대시보드'), allow: [], operationsOnly: true },
    { to: '/dashboard', label: t('유저 관리'), allow: ['lv1', 'lv2', 'lv3', 'lv4'] },
    { to: '/tables', label: t('테이블 관리'), allow: ['lv1'] },
    { to: '/daily-mission', label: t('오늘의 미션'), allow: ['lv1'] },
    { to: '/marquee', label: t('전광판'), allow: ['lv1'] },
    { to: '/referrals', label: t('에이전트 가입 현황'), allow: ['lv1', 'lv2', 'lv3', 'lv4'] },
    { to: '/ledger', label: t('자산기록'), allow: ['lv1', 'lv2', 'lv3', 'lv4'] },
    { to: '/payment', label: t('결제관리'), allow: ['lv1', 'lv2', 'lv3', 'lv4'] },
    { to: '/payment-operations', label: t('실시간 결제상황'), allow: [], superAdminOnly: true },
    { to: '/payment-policy', label: t('결제 정책 설정'), allow: [], superAdminOnly: true },
    { to: '/lounge', label: t('라운지 관리'), allow: ['lv1', 'lv2', 'lv3', 'lv4'], loungeOnly: true },
    { to: '/lounge-permissions', label: '권한 설정', allow: [], superAdminOnly: true },
    { to: '/notice', label: t('공지 관리'), allow: ['lv1'] },
    { to: '/ads', label: t('광고 관리'), allow: ['lv1'] },
    { to: '/email', label: t('이메일 관리'), allow: [], superAdminOnly: true },
  ];

  const visibleMenuItems = menuItems.filter((item) =>
    item.superAdminOnly ? isSuperAdmin : item.operationsOnly ? hasOperationsDashboard : item.loungeOnly ? loungeCanView : item.allow.includes(userLevel)
  );
  const currentMenuItem =
    visibleMenuItems.find((item) => item.to === location.pathname) || visibleMenuItems[0];
  const currentPageLabel = currentMenuItem?.label || 'Admin';
  const desktopPrimaryMenuItems = visibleMenuItems.slice(0, desktopPrimaryCount);
  const desktopMoreMenuItems = visibleMenuItems.slice(desktopPrimaryCount);
  const isDesktopMoreActive = desktopMoreMenuItems.some((item) => item.to === location.pathname);

  React.useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDesktopMoreOpen(false);
  }, [location.pathname]);

  React.useLayoutEffect(() => {
    if (hideNav || !desktopNavRef.current || !desktopMeasureRef.current) return undefined;

    const navElement = desktopNavRef.current;
    const measureElement = desktopMeasureRef.current;

    const recalculateDesktopMenu = () => {
      const availableWidth = navElement.clientWidth;
      const menuNodes = Array.from(measureElement.querySelectorAll('[data-menu-measure-item]'));
      const moreNode = measureElement.querySelector('[data-menu-measure-more]');
      if (!availableWidth || menuNodes.length === 0 || !moreNode) return;

      const gap = 3;
      const widths = menuNodes.map((node) => node.getBoundingClientRect().width);
      const allItemsWidth = widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, widths.length - 1);
      if (allItemsWidth <= availableWidth) {
        setDesktopPrimaryCount(widths.length);
        return;
      }

      const moreWidth = moreNode.getBoundingClientRect().width;
      let used = 0;
      let count = 0;
      for (let index = 0; index < widths.length; index += 1) {
        const nextUsed = used + (count > 0 ? gap : 0) + widths[index];
        const requiredWithMore = nextUsed + gap + moreWidth;
        if (requiredWithMore > availableWidth) break;
        used = nextUsed;
        count += 1;
      }

      setDesktopPrimaryCount(Math.max(1, count));
    };

    recalculateDesktopMenu();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(recalculateDesktopMenu)
      : null;
    observer?.observe(navElement);
    window.addEventListener('resize', recalculateDesktopMenu);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', recalculateDesktopMenu);
    };
  }, [hideNav, isSuperAdmin, userLevel, loungeCanView]);

  React.useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  React.useEffect(() => {
    if (!isDesktopMoreOpen) return undefined;
    const handlePointerDown = (event) => {
      if (desktopMoreRef.current && !desktopMoreRef.current.contains(event.target)) {
        setIsDesktopMoreOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsDesktopMoreOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDesktopMoreOpen]);

  return (
    <div className={hideNav ? 'auth-shell' : 'admin-app-shell'}>
      {!hideNav && (
        <header className="admin-header">
          <div className="admin-header-inner">
            <NavLink to={homePath} className="admin-brand" aria-label={t('관리자 홈')}>
              <BrandLogo />
            </NavLink>

            <nav ref={desktopNavRef} className="admin-desktop-nav" aria-label={t('관리자 메뉴')}>
              <div ref={desktopMeasureRef} className="admin-desktop-menu-measure" aria-hidden="true">
                {visibleMenuItems.map((item) => (
                  <span key={`measure-${item.to}`} className="admin-nav-link" data-menu-measure-item>{item.label}</span>
                ))}
                <span className="admin-nav-link admin-desktop-more-button" data-menu-measure-more>{t('더보기')} ▾</span>
              </div>
              {desktopPrimaryMenuItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `admin-nav-link ${isActive ? 'is-active' : ''}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              {desktopMoreMenuItems.length > 0 && (
                <div ref={desktopMoreRef} className="admin-desktop-more-wrap">
                  <button
                    type="button"
                    className={`admin-nav-link admin-desktop-more-button ${isDesktopMoreActive ? 'is-active' : ''}`}
                    onClick={() => setIsDesktopMoreOpen((open) => !open)}
                    aria-expanded={isDesktopMoreOpen}
                  >
                    {t('더보기')} <span aria-hidden="true">{isDesktopMoreOpen ? '▴' : '▾'}</span>
                  </button>
                  {isDesktopMoreOpen && (
                    <div className="admin-desktop-more-menu">
                      {desktopMoreMenuItems.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={() => setIsDesktopMoreOpen(false)}
                          className={({ isActive }) => `admin-desktop-more-link ${isActive ? 'is-active' : ''}`}
                        >
                          {item.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </nav>

            <div className="admin-header-tools">
              <AdminAssetSummary />
              <LanguageSelect className="admin-language-select" />
              <button type="button" onClick={handleLogout} className="admin-logout-button">
                {t('로그아웃')}
              </button>
            </div>

            <div ref={mobileMenuRef} className="admin-mobile-menu-wrap">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen((open) => !open)}
                aria-expanded={isMobileMenuOpen}
                aria-label={t('관리자 메뉴 열기')}
                className="admin-mobile-menu-button"
              >
                <span>{currentPageLabel}</span>
                <i aria-hidden="true">{isMobileMenuOpen ? '×' : '☰'}</i>
              </button>

              {isMobileMenuOpen && (
                <div className="admin-mobile-menu">
                  <div className="admin-mobile-brand-row">
                    <BrandLogo compact />
                    <span>Operations Center</span>
                  </div>

                  <div className="admin-mobile-nav-list">
                    {visibleMenuItems.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `admin-mobile-nav-link ${isActive ? 'is-active' : ''}`
                        }
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>

                  <div className="admin-mobile-tools">
                    <AdminAssetSummary compact className="w-full" />
                    <LanguageSelect className="admin-language-select w-full" />
                    <button type="button" onClick={handleLogout} className="admin-logout-button w-full">
                      {t('로그아웃')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      <main className={hideNav ? 'auth-main' : 'admin-main'}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          <Route
            path="/operations-dashboard"
            element={
              <OperationsDashboardPermissionRoute>
                <OperationsDashboard />
              </OperationsDashboardPermissionRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <AuthRoute>
                <Dashboard />
              </AuthRoute>
            }
          />

          <Route
            path="/tables"
            element={
              <LevelRoute allow={['lv1']}>
                <TableManagement />
              </LevelRoute>
            }
          />

          <Route
            path="/daily-mission"
            element={
              <LevelRoute allow={['lv1']}>
                <DailyMissionManagement />
              </LevelRoute>
            }
          />

          <Route
            path="/marquee"
            element={
              <LevelRoute allow={['lv1']}>
                <MarqueeManagement />
              </LevelRoute>
            }
          />

          <Route
            path="/referrals"
            element={
              <LevelRoute allow={['lv1', 'lv2', 'lv3', 'lv4']}>
                <AgentReferralManagement />
              </LevelRoute>
            }
          />

          <Route
            path="/ledger"
            element={
              <AuthRoute>
                <AssetLedger />
              </AuthRoute>
            }
          />

          <Route
            path="/payment"
            element={
              <AuthRoute>
                <PaymentPage />
              </AuthRoute>
            }
          />


          <Route
            path="/payment-operations"
            element={
              <SuperAdminRoute>
                <LivePaymentOperations />
              </SuperAdminRoute>
            }
          />

          <Route
            path="/payment-policy"
            element={
              <SuperAdminRoute>
                <PaymentPolicySettings />
              </SuperAdminRoute>
            }
          />

          <Route
            path="/lounge"
            element={
              <LoungePermissionRoute>
                <LoungeManagement />
              </LoungePermissionRoute>
            }
          />

          <Route path="/lounge-permissions" element={<SuperAdminRoute><LoungePermissionSettings /></SuperAdminRoute>} />

          <Route
            path="/notice"
            element={
              <LevelRoute allow={['lv1']}>
                <NoticeManagement />
              </LevelRoute>
            }
          />

          <Route
            path="/ads"
            element={
              <LevelRoute allow={['lv1']}>
                <AdBannerManagement />
              </LevelRoute>
            }
          />

          <Route
            path="/email"
            element={
              <SuperAdminRoute>
                <EmailManagement />
              </SuperAdminRoute>
            }
          />

          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route
            path="*"
            element={token ? <Navigate to={homePath} replace /> : <Navigate to="/login" replace />}
          />
        </Routes>
      </main>
    </div>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <AdminMaintenanceGate><App /></AdminMaintenanceGate>
    </Router>
  );
}
